
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import vModal from "@/components/vModal.vue";
import InteractiveGiftCard from "@/components/shared/InteractiveGiftCard.vue";
import SelectableIconCard from "@/components/shared/SelectableIconCard.vue";
import { ExitSurveyDto } from "@/models/Subscriptions/ExitSurveyDto";
import { CancellationReason } from "@/models/Subscriptions/CancellationReason";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import { LocalizationState } from "@/store/modules/LocalizationModule";
import CancellationWorkflowRepository from "@/utils/API/CancellationWorkflowRepository";
import {
  ClaimRetentionOfferDto,
  PackageConfiguration,
  RetentionEmailSubmissionResultDto,
  SubmitCancellationFeedbackDto,
  SubmitFeatureRequestDto,
  SubmitSupportCallRequestDto,
  defaultAddonOptions,
  resolveWorkflowConfiguration,
} from "@/models/CancellationWorkflow";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import Package from "@/models/Subscriptions/SubscriptionPackages/Package";
import { SubscriptionRecurrenceType } from "@/models/Subscriptions/SubscriptionRecurrenceType";
import { AccountState } from "@/store/modules/AccountModule";

type ReasonId =
  | "too-expensive"
  | "not-using-enough"
  | "temporary-closure"
  | "switching-solution"
  | "missing-features"
  | "difficult-to-set-up"
  | "poor-support-experience"
  | "other";

type Step = "reason" | "offer" | "confirm" | "done";

type Offer =
  | { kind: "discount"; eyebrow: string; title: string; subtitle: string }
  | { kind: "downgrade"; eyebrow: string; title: string; subtitle: string }
  | { kind: "feedback"; eyebrow: string; title: string; subtitle: string; bullets: string[] }
  | { kind: "support"; eyebrow: string; title: string; subtitle: string; bullets: string[] }
  | { kind: "generic"; eyebrow: string; title: string; subtitle: string };

type RetentionOfferDetails = {
  title: string;
  packageName: string;
  content: string;
  originalPrice: number | null;
  currentPayingAmount: number | null;
  discountedPrice: number | null;
  billingPeriod: string;
  couponDescription: string;
  couponCode: string;
  bullets: string[];
  discountPercentLabel: string;
  discountPercent: number | null;
  discountContextLabel: string;
  currentPackageTag: string;
  isCurrentPackage: boolean;
  canClaim: boolean;
  claimStatus: string;
  claimMessage: string;
  hasActiveRetentionOffer: boolean;
  historicalClaimedPrices: number[];
};

type OfferComparisonSource = {
  originalPrice: number | null;
  currentPayingAmount: number | null;
  discountedPrice: number | null;
  historicalClaimedPrices: number[];
};

type DiscountAddonOption = {
  value: AddOnType;
  title: string;
  description: string;
  icon: string;
};

type ClaimCompletionResult = {
  claimed: boolean;
  claimStatus: string;
  couponCode: string;
  message: string;
  couponId?: number | null;
  retentionOfferClaimId: number;
  discountedNextPaymentAmount?: number | null;
  remainingRenewalCount: number;
  appliesFromNextPayment: boolean;
};

@Component({
  components: {
    InteractiveGiftCard,
    vModal,
    SelectableIconCard,
  },
  computed: {
    ...mapState({
      accountState: (state: any) => state.AccountModule,
      localizationState: (state: any) => state.LocalizationModule,
    }),
  },
})
export default class vRetentionCancellationFlow extends Vue {
  @Prop() showFlowModal!: boolean;
  @Prop({ default: null }) subscriptionEndDate!: string | null;
  @Prop({ default: 0 }) userId!: number;
  @Prop({ default: 0 }) subscriptionPackageId!: number;
  @Prop({ default: 0 }) restaurantId!: number;
  @Prop({ default: "" }) planName!: string;
  @Prop({ default: 0 }) planPrice!: number;
  @Prop({ default: (): Package[] => [] }) availablePackages!: Package[];
  @Prop({ default: null }) currentSubscriptionCycle!: SubscriptionRecurrenceType | null;
  accountState!: AccountState;
  localizationState!: LocalizationState;

  currentStep: Step = "reason";
  selectedReason: ReasonId | null = null;
  feedback: string = "";
  workflowConfiguration: PackageConfiguration | null = null;
  isClaimSubmitting = false;
  hasClaimedRetentionOffer = false;
  claimedRetentionResult: ClaimCompletionResult | null = null;
  featureRequestSubmitted = false;
  bookCallSubmitted = false;
  isFeatureRequestSubmitting = false;
  isBookCallSubmitting = false;
  featureRequestSubmissionResult: RetentionEmailSubmissionResultDto | null = null;
  bookCallSubmissionResult: RetentionEmailSubmissionResultDto | null = null;
  showFeatureRequestModal = false;
  showBookCallModal = false;
  showClaimOfferModal = false;
  showAddonChoiceModal = false;
  couponCopied = false;
  revealedGiftIndexes: number[] = [];
  unboxedGiftIndexes: number[] = [];
  addonChoiceIndex: number | null = null;
  selectedDiscountedAddons: { [key: number]: DiscountAddonOption } = {};
  giftAutoOpenTimeout: ReturnType<typeof setTimeout> | null = null;

  featureRequest = {
    name: "",
    email: "",
    details: "",
  };

  supportBooking = {
    name: "",
    email: "",
    phone: "",
    availableDate: "",
    details: "",
  };

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common", "cancellation_workflow"],
    });
  }

  get translations() {
    return this.localizationState.translations;
  }

  tr(key: string, fallback: string) {
    const translated = this.translations.get(key);
    if (typeof translated !== "string") {
      return fallback;
    }

    const normalizedTranslation = translated.trim();
    if (!normalizedTranslation || /^-+$/.test(normalizedTranslation)) {
      return fallback;
    }

    return translated;
  }

  get addonOptionCatalog(): DiscountAddonOption[] {
    const sugarAlcoholTitle = this.tr("cw_addon_sugar_alcohols", "Sugar Alcohol Addon");
    const sugarAlcoholDescription = this.tr("cw_addon_sugar_alcohols_desc", "Add Sugar Alcohol tools at no charge.");

    return defaultAddonOptions.map((addon) => {
      switch (addon.value) {
      case AddOnType.AllergenAI:
        return {
          value: addon.value,
          title: this.tr("cw_addon_allergen_ai", "Allergen AI Addon"),
          description: this.tr("cw_addon_allergen_ai_desc", "Add Allergen AI at no charge."),
          icon: "fa fa-magic",
        };
      case AddOnType.RecipeCost:
        return {
          value: addon.value,
          title: this.tr("cw_addon_recipe_cost", "Recipe Cost Addon"),
          description: this.tr("cw_addon_recipe_cost_desc", "Add Recipe Cost at no charge."),
          icon: "fa fa-calculator",
        };
      case AddOnType.SugarAlcohols:
        return {
          value: addon.value,
          title: sugarAlcoholTitle,
          description: sugarAlcoholDescription,
          icon: "fa fa-flask",
        };
      case AddOnType.WellnessModule:
        return {
          value: addon.value,
          title: this.tr("cw_addon_wellness", "Wellness Analyzer Addon"),
          description: this.tr("cw_addon_wellness_desc", "Add Wellness Analyzer at no charge."),
          icon: "fa fa-heartbeat",
        };
      default:
        return {
          value: addon.value,
          title: addon.label,
          description: addon.description,
          icon: addon.icon,
        };
      }
    });
  }

  get couponDescriptions(): { [key: string]: string } {
    return {
      SAVE15NEXT6: this.tr("cw_coupon_save15next6", "15% off for the next 6 months"),
      SAVE40NEXT3: this.tr("cw_coupon_save40next3", "40% off for the next 3 months"),
      SUPPORTMAKEGOOD: "",
    };
  }

  get reasons() {
    return [
      { id: "too-expensive", label: this.tr("cw_reason_too_expensive", "Too expensive"), icon: "fa fa-money" },
      { id: "not-using-enough", label: this.tr("cw_reason_not_using_enough", "Not using MenuSano enough"), icon: "fa fa-line-chart" },
      { id: "temporary-closure", label: this.tr("cw_reason_temporary_closure", "Temporary business closure"), icon: "fa fa-pause-circle-o" },
      { id: "missing-features", label: this.tr("cw_reason_missing_features", "Missing features"), icon: "fa fa-lightbulb-o" },
      { id: "switching-solution", label: this.tr("cw_reason_switching_solution", "Switching to another solution"), icon: "fa fa-exchange" },
      { id: "difficult-to-set-up", label: this.tr("cw_reason_difficult_setup", "Difficult to set up"), icon: "fa fa-cogs" },
      { id: "poor-support-experience", label: this.tr("cw_reason_poor_support", "Poor support experience"), icon: "fa fa-headphones" },
      { id: "other", label: this.tr("cw_reason_other", "Other"), icon: "fa fa-commenting-o" },
    ];
  }

  get modalTitle() {
    if (this.currentStep === "done") {
      return this.hasClaimedRetentionOffer ? this.tr("cw_offer_claimed", "Offer claimed") : this.tr("cw_back_to_subscription", "Back to subscription");
    }

    if (this.currentStep === "reason" || this.currentStep === "offer") {
      return this.tr("cw_before_you_go", "Before you go");
    }

    if (this.currentStep === "confirm") {
      return this.tr("cw_confirm_cancellation", "Confirm cancellation");
    }

    return this.tr("cw_understand_title", "We'd like to understand.");
  }

  get stepIndex() {
    if (!this.hasConfiguredRetentionOffer) {
      if (this.currentStep === "confirm" || this.currentStep === "done") {
        return 2;
      }

      return 1;
    }

    if (this.currentStep === "offer") {
      return 2;
    }

    if (this.currentStep === "confirm" || this.currentStep === "done") {
      return 3;
    }

    return 1;
  }

  get progressStepCount() {
    return this.hasConfiguredRetentionOffer ? 3 : 2;
  }

  get fallbackPackageName() {
    return this.planName || `Package ${this.subscriptionPackageId}`;
  }

  get showDetailsField() {
    return this.selectedReason === "other" && this.currentStep === "offer" && this.activeOffer.kind === "generic";
  }

  get detailsLabel() {
    return this.tr("cw_details_label", "Please provide a bit more detail");
  }

  get detailsPlaceholder() {
    return this.tr("cw_details_placeholder", "Share any details that would help us understand your experience.");
  }

  get lighterPlanOffer(): Offer {
    return {
      kind: "discount",
      eyebrow: this.tr("cw_eyebrow_personalized_offer", "Personalized offer"),
      title: this.tr("cw_offer_lighter_title", "We'd love to keep you. Would a lower-cost option help?"),
      subtitle: this.tr("cw_offer_lighter_subtitle", "Pick the plan that works best for your team right now, no strings attached."),
    };
  }

  get feedbackOffer(): Offer {
    return {
      kind: "feedback",
      eyebrow: this.tr("cw_feedback_eyebrow", "We're actively improving the platform"),
      title: this.tr("cw_feedback_title", "We'd love to build that for you."),
      subtitle: this.tr("cw_feedback_subtitle", "Did you know? We can build custom features just for you. Tell us what you need and we'll make it happen."),
      bullets: [
        this.tr("cw_feedback_bullet_1", "Upcoming feature: Allergen Report"),
        this.tr("cw_feedback_bullet_2", "Recently shipped: AI Allergen Detect"),
        this.tr("cw_feedback_bullet_3", "Request the feature you need directly from this screen"),
      ],
    };
  }

  get supportOffer(): Offer {
    return {
      kind: "support",
      eyebrow: this.tr("cw_support_eyebrow", "We can help fix this"),
      title: this.tr("cw_support_title", "We would love to hear your concerns."),
      subtitle: this.tr("cw_support_subtitle", "Tell us the issue, and we will address your concerns."),
      bullets: [
        this.tr("cw_support_bullet_1", "Open support chat"),
        this.tr("cw_support_bullet_2", "Book a call with the team"),
        this.tr("cw_support_bullet_3", "Get one free month after resolution when appropriate"),
      ],
    };
  }

  get switchingOffer(): Offer {
    return {
      kind: "discount",
      eyebrow: this.tr("cw_switching_eyebrow", "One personalized offer"),
      title: this.tr("cw_switching_title", "What are they offering that we’re not?"),
      subtitle: this.tr("cw_switching_subtitle", "Knowing what’s pulling you away helps us get better. Take 30 seconds to tell us, it might be something we can build for you."),
    };
  }

  get genericOffer(): Offer {
    return {
      kind: "generic",
      eyebrow: this.tr("cw_generic_eyebrow", "Flexible option"),
      title: this.tr("cw_generic_title", "Could you tell us a bit more?"),
      subtitle: this.tr("cw_generic_subtitle", "Is it us? Did we fail somewhere? Let us know so we can make MenuSano better for our customers."),
    };
  }

  get discountOfferDetails(): RetentionOfferDetails {
    const discountOffer = this.resolvedWorkflowCard?.discountOffer;
    return this.buildOfferDetails(
      discountOffer,
      this.tr("cw_current_package_offer", "Current package offer"),
      this.planName || discountOffer?.title || this.tr("cw_current_package", "Current package"),
      this.getRetentionSupportingLine(discountOffer),
      discountOffer?.originalPrice ?? this.currentOfferBasePrice,
      true,
      typeof discountOffer?.originalPrice === "number"
    );
  }

  get currentCycle() {
    if (this.currentSubscriptionCycle !== null && this.currentSubscriptionCycle !== undefined) {
      return this.currentSubscriptionCycle;
    }

    const currentPackage = this.availablePackages.find((pkg) => pkg.id === this.subscriptionPackageId);
    return currentPackage?.cycle ?? null;
  }

  get resolvedWorkflowCard() {
    if (!this.workflowConfiguration) {
      return null;
    }

    return this.workflowConfiguration.cardConfigurations.find((card) => card.id === "personalized-offer") || null;
  }

  get hasConfiguredRetentionOffer() {
    const offer = this.resolvedWorkflowCard?.discountOffer;
    if (!offer) {
      return false;
    }

    const hasCoupon = !!offer.couponCode?.trim?.();
    const hasConfiguredGift = Array.isArray(offer.selectedGiftSlots)
      && offer.selectedGiftSlots.some((slot: string | null) => !!slot);
    const hasConfiguredPrice = typeof offer.discountedPrice === "number" || typeof offer.originalPrice === "number";

    return hasCoupon || hasConfiguredGift || hasConfiguredPrice;
  }

  get effectiveUserId() {
    return this.accountState?.userDetails?.userId || this.userId || 0;
  }

  get effectiveRestaurantId() {
    return this.accountState?.userDetails?.restaurantId || this.restaurantId || 0;
  }

  get effectiveSubscriptionPackageId() {
    return this.accountState?.userDetails?.subscriptionPackageId || this.subscriptionPackageId || 0;
  }

  get currentUserName() {
    const firstName = this.accountState?.userDetails?.firstName?.trim?.() || "";
    const lastName = this.accountState?.userDetails?.lastName?.trim?.() || "";
    return `${firstName} ${lastName}`.trim();
  }

  get currentUserEmail() {
    return this.accountState?.userDetails?.email || "";
  }

  get activeOffer(): Offer {
    switch (this.selectedReason) {
    case "too-expensive":
    case "not-using-enough":
    case "temporary-closure":
      return this.lighterPlanOffer;
    case "difficult-to-set-up":
    case "poor-support-experience":
      return this.supportOffer;
    case "missing-features":
      return this.feedbackOffer;
    case "switching-solution":
      return this.switchingOffer;
    case "other":
      return this.genericOffer;
    default:
      return this.genericOffer;
    }
  }

  get showFeatureRequestActions() {
    return this.activeOffer.kind === "feedback" || this.selectedReason === "switching-solution";
  }

  get hasStaySubscribedFooterAction() {
    return this.activeOffer.kind === "support"
      || this.showFeatureRequestActions
      || !this.hasConfiguredRetentionOffer
      || this.activeOffer.kind === "generic";
  }

  get shouldShowKeepSubscriptionAction() {
    return this.currentStep === "offer"
      && this.hasConfiguredRetentionOffer
      && !this.hasStaySubscribedFooterAction;
  }

  get planBillingPeriodLabel() {
    return this.currentCycle === SubscriptionRecurrenceType.Yearly ? "year" : "month";
  }

  get formattedEndDate() {
    if (!this.subscriptionEndDate) {
      return this.tr("cw_your_renewal_date", "your renewal date");
    }

    return this.$moment(this.subscriptionEndDate).format("MMM DD, YYYY");
  }

  get claimOfferDetails() {
    const selectedOfferConfiguration = this.resolvedWorkflowCard?.discountOffer;
    const selectedOffer = this.discountOfferDetails;
    const fallbackDiscountCopy = this.tr("cw_retention_offer_available", "Retention offer available");
    const fallbackSummary = this.tr("cw_retention_offer_summary", "Keep your subscription active with a reduced rate while we help you get more value from MenuSano.");

    return {
      badge: this.tr("cw_recommended_offer", "Recommended offer"),
      title: selectedOffer?.title || this.planName || this.tr("cw_current_package", "Current package"),
      packageName: selectedOffer?.packageName || this.planName || this.tr("cw_current_package", "Current package"),
      summary: selectedOffer?.content || fallbackSummary,
      couponCode: selectedOffer?.couponCode || "",
      discountCopy: selectedOffer?.couponDescription || this.getCouponDescription(selectedOffer?.couponCode) || fallbackDiscountCopy,
      originalPrice: selectedOffer?.originalPrice ?? null,
      discountedPrice: selectedOffer?.discountedPrice ?? null,
      billingPeriod: selectedOffer?.billingPeriod || this.planBillingPeriodLabel,
      discountPercentLabel: selectedOffer?.discountPercentLabel || "",
      discountContextLabel: selectedOffer?.discountContextLabel || this.tr("cw_discount_context_personalized", "PERSONALIZED NEXT RATE"),
      currentPackageTag: selectedOffer?.currentPackageTag || "",
      currentPayingAmount: selectedOffer?.currentPayingAmount ?? null,
      canClaim: selectedOffer?.canClaim ?? true,
      claimStatus: selectedOffer?.claimStatus || "",
      claimMessage: selectedOffer?.claimMessage || "",
      hasActiveRetentionOffer: selectedOffer?.hasActiveRetentionOffer ?? false,
      historicalClaimedPrices: Array.isArray(selectedOffer?.historicalClaimedPrices) ? selectedOffer.historicalClaimedPrices : [],
      note: `${this.tr("cw_offer_note_prefix", "This offer applies on your next payment date and continues") } ${this.getOfferValidityLabel(selectedOfferConfiguration?.validityPeriodCount, selectedOfferConfiguration?.validityPeriodUnit)}.`,
    };
  }

  get shouldShowClaimActionMessage() {
    return !this.claimOfferDetails.canClaim && !!this.claimOfferDetails.claimMessage;
  }

  get claimActionMessage() {
    return this.claimOfferDetails.claimMessage;
  }

  get claimActionTitle() {
    switch (this.claimOfferDetails.claimStatus) {
    case "ClaimLimitReached":
      return this.tr("cw_claim_limit_reached", "Claim limit reached");
    case "BelowMinimumFinalPrice":
      return this.tr("cw_offer_unavailable", "Offer unavailable");
    case "CouponInvalid":
      return this.tr("cw_offer_unavailable", "Offer unavailable");
    case "NoActiveSubscription":
      return this.tr("cw_offer_unavailable", "Offer unavailable");
    case "Claimed":
      return this.tr("cw_offer_claimed", "Offer claimed");
    default:
      return this.tr("cw_offer_unavailable", "Offer unavailable");
    }
  }

  get completionHeading() {
    return this.hasClaimedRetentionOffer
      ? this.tr("cw_completion_heading_stay", "Thanks for staying with us")
      : this.tr("cw_completion_heading_cancel", "We're sorry to see you go, but thanks for being with us!");
  }

  get completionCopy() {
    if (this.hasClaimedRetentionOffer && this.claimedRetentionResult) {
      const priceCopy = typeof this.claimedRetentionResult.discountedNextPaymentAmount === "number"
        ? ` at ${this.formatCurrency(this.claimedRetentionResult.discountedNextPaymentAmount)}`
        : "";

      return `${this.tr("cw_completion_copy_stay_prefix", "Your personalized offer has been saved. Starting from your next payment, your subscription will renew")}${priceCopy} ${this.tr("cw_completion_copy_stay_middle", "for the next")} ${this.claimedRetentionResult.remainingRenewalCount} ${this.tr("cw_completion_copy_stay_payment", "payment")}${this.claimedRetentionResult.remainingRenewalCount === 1 ? "" : "s"}.`;
    }

    return `${this.tr("cw_completion_copy_cancel_prefix", "Your subscription will end on")} ${this.formattedEndDate}. ${this.tr("cw_completion_copy_cancel_suffix", "You'll still have full access until then.")}`;
  }

  get completionLabel() {
    return this.tr("cw_what_happens_next", "What happens next");
  }

  getCouponDescription(couponCode?: string | null) {
    if (!couponCode) {
      return "";
    }

    return this.couponDescriptions[couponCode] || "";
  }

  get addonOptions() {
    const option = this.resolvedWorkflowCard?.discountOffer;
    const configuredValues = (option?.availableAddons || [])
      .map((addonValue) => this.normalizeAddonValue(addonValue))
      .filter((addonValue): addonValue is AddOnType => addonValue !== null);
    return this.addonOptionCatalog.filter((addon) => configuredValues.includes(addon.value));
  }

  get giftRewards() {
    return this.getConfiguredGiftRewards();
  }

  get currentPackageDefinition() {
    return this.availablePackages.find(
      (pkg) =>
        pkg.id === this.effectiveSubscriptionPackageId
        || (
          pkg.externalId === this.accountState?.userDetails?.subscriptionPackageExternalId
          && pkg.cycle === this.currentCycle
        )
    ) || null;
  }

  get currentRecurringAddonCodes() {
    const addOns = this.accountState?.userDetails?.addOns || [];
    return addOns
      .filter((addOn: any) => addOn?.isRecurring)
      .map((addOn: any) => this.normalizeAddonValue(addOn?.addOn))
      .filter((addOnCode: AddOnType | null): addOnCode is AddOnType => addOnCode !== null);
  }

  get currentPaidUserLicenseTotal() {
    const currentPackage = this.currentPackageDefinition;
    const paidUserLicenses = this.accountState?.userDetails?.paidUserLicenses || 0;

    if (!currentPackage || !paidUserLicenses) {
      return 0;
    }

    const matchingRange = currentPackage.userLicensePricingRanges?.find((range: any) =>
      range.from <= paidUserLicenses && (range.to === null || range.to === undefined || range.to >= paidUserLicenses)
    );

    return (matchingRange?.price || 0) * paidUserLicenses;
  }

  get currentRecurringAddonTotal() {
    const currentPackage = this.currentPackageDefinition;
    if (!currentPackage) {
      return 0;
    }

    return this.currentRecurringAddonCodes.reduce((total, addOnCode) => {
      const configuredAddon = currentPackage.addOns?.find((addOn) => addOn.addOn === addOnCode);
      return total + (configuredAddon?.price || 0);
    }, 0);
  }

  get selectedFreeAddonCredit() {
    const currentPackage = this.currentPackageDefinition;
    if (!currentPackage) {
      return 0;
    }

    return this.getSelectedAddonCodes().reduce((total, addOnCode) => {
      if (!this.currentRecurringAddonCodes.includes(addOnCode)) {
        return total;
      }

      const configuredAddon = currentPackage.addOns?.find((addOn) => addOn.addOn === addOnCode);
      return total + (configuredAddon?.price || 0);
    }, 0);
  }

  get currentOfferBasePrice() {
    const currentPackage = this.currentPackageDefinition;
    const packagePrice = currentPackage?.price ?? this.planPrice ?? 0;
    const currentTotal = packagePrice + this.currentRecurringAddonTotal + this.currentPaidUserLicenseTotal;
    const adjustedTotal = currentTotal - this.selectedFreeAddonCredit;

    return Math.max(0, Math.round(adjustedTotal * 100) / 100);
  }

  get isFeatureRequestValid() {
    return !!this.featureRequest.name && !!this.featureRequest.email && !!this.featureRequest.details;
  }

  get isBookCallValid() {
    return !!this.supportBooking.name
      && !!this.supportBooking.email
      && !!this.supportBooking.phone
      && !!this.supportBooking.availableDate
      && !!this.supportBooking.details;
  }

  get requiredAddonGiftIndexes() {
    return this.giftRewards
      .map((gift, index) => ({ gift, index }))
      .filter(({ gift }) => gift.kind === "addon")
      .map(({ index }) => index);
  }

  get isClaimOfferBlocked() {
    return this.requiredAddonGiftIndexes.some((index) => !this.selectedDiscountedAddons[index]);
  }

  get claimOfferComparisonPrices() {
    return this.getOfferComparisonPrices(this.claimOfferDetails);
  }

  resetFlow() {
    this.clearGiftAutoOpenTimeout();
    this.currentStep = "reason";
    this.selectedReason = null;
    this.feedback = "";
    this.isClaimSubmitting = false;
    this.hasClaimedRetentionOffer = false;
    this.claimedRetentionResult = null;
    this.revealedGiftIndexes = [];
    this.unboxedGiftIndexes = [];
    this.selectedDiscountedAddons = {};
    this.closeAddonChoiceModal();
    this.closeClaimOfferModal();
    this.closeFeatureRequestModal();
    this.closeBookCallModal();
    this.loadWorkflowConfiguration();
  }

  @Watch("subscriptionPackageId")
  onSubscriptionPackageIdChanged() {
    if (this.showFlowModal) {
      this.loadWorkflowConfiguration();
    }
  }

  @Watch("showFlowModal")
  onShowFlowModalChanged(showFlowModal: boolean) {
    if (showFlowModal) {
      this.loadWorkflowConfiguration();
      return;
    }

    this.clearGiftAutoOpenTimeout();
  }

  @Watch("currentStep")
  onCurrentStepChanged(currentStep: Step) {
    if (currentStep === "offer") {
      this.scheduleGiftAutoOpen();
      return;
    }

    this.clearGiftAutoOpenTimeout();
  }

  loadWorkflowConfiguration() {
    if (!this.subscriptionPackageId) {
      this.workflowConfiguration = null;
      return;
    }

    const defaultConfiguration = this.buildDefaultWorkflowConfiguration();

    CancellationWorkflowRepository.getConfiguration({
      subscriptionPackageId: this.subscriptionPackageId,
      restaurantId: this.effectiveRestaurantId || null,
    })
      .then((response) => {
        if (!response.data.result?.configuration) {
          this.workflowConfiguration = defaultConfiguration;
          return;
        }

        this.workflowConfiguration = resolveWorkflowConfiguration(
          response.data.result.configuration,
          this.subscriptionPackageId,
          this.fallbackPackageName
        );
      })
      .catch(() => {
        this.workflowConfiguration = defaultConfiguration;
      });
  }

  handleClose() {
    this.$emit("on-cancel");
  }

  goToOfferStep() {
    if (!this.selectedReason) {
      return;
    }

    this.currentStep = this.getNextStepAfterReasonSelection();
    this.trackRetentionAttempt();
  }

  goBackFromConfirm() {
    this.currentStep = this.hasConfiguredRetentionOffer ? "offer" : "reason";
  }

  clearGiftAutoOpenTimeout() {
    if (this.giftAutoOpenTimeout) {
      clearTimeout(this.giftAutoOpenTimeout);
      this.giftAutoOpenTimeout = null;
    }
  }

  scheduleGiftAutoOpen() {
    this.clearGiftAutoOpenTimeout();

    this.$nextTick(() => {
      this.giftAutoOpenTimeout = setTimeout(() => {
        this.autoOpenVisibleGiftBoxes();
        this.giftAutoOpenTimeout = null;
      }, 1000);
    });
  }

  autoOpenVisibleGiftBoxes() {
    this.giftRewards.forEach((gift, index) => {
      this.handleGiftClick(gift, index);
    });
  }

  async submitCancellation() {
    if (!this.selectedReason) {
      this.currentStep = "reason";
      return;
    }

    const trimmedFeedback = this.feedback.trim();
    if (trimmedFeedback) {
      try {
        const response = await CancellationWorkflowRepository.submitCancellationFeedback(this.buildCancellationFeedbackRequest(trimmedFeedback));
        if (response?.data?.result?.accepted && process.env.NODE_ENV !== "production") {
          console.info("Cancellation feedback accepted", trimmedFeedback);
        }
      } catch (error) {
      }
    }

    this.trackRetentionAttempt("cancelled");
    this.$emit("survey-submitted", this.buildExitSurveyDto(false));
    this.currentStep = "done";
  }

  acceptRetentionOffer() {
    this.handleRetentionChoice();
  }

  handleRetentionChoice() {
    if (!this.selectedReason) {
      this.handleClose();
      return;
    }

    if (this.activeOffer.kind === "support") {
      this.trackRetentionAttempt("follow-up-pending");
    }

    this.$emit("retention-selected", {
      exitSurvey: this.buildExitSurveyDto(true),
      claimRequest: null,
    });
  }

  openClaimOfferModal() {
    if (!this.hasConfiguredRetentionOffer) {
      return;
    }

    if (!this.claimOfferDetails.canClaim) {
      if (this.$snotify?.info) {
        this.$snotify.info(this.claimOfferDetails.claimMessage || "This offer is already active on your subscription.");
      }
      return;
    }

    if (this.isClaimOfferBlocked) {
      return;
    }

    this.couponCopied = false;
    this.showClaimOfferModal = true;
  }

  closeClaimOfferModal() {
    this.showClaimOfferModal = false;
    this.couponCopied = false;
  }

  openFeatureRequestModal() {
    this.prefillContactDetails(this.featureRequest);

    this.showFeatureRequestModal = true;
  }

  openBookCallModal() {
    this.prefillContactDetails(this.supportBooking);

    this.showBookCallModal = true;
  }

  applyClaimOffer() {
    if (this.isClaimOfferBlocked) {
      return;
    }

    const claimRequest = this.buildClaimRequest();
    if (!claimRequest || !this.selectedReason) {
      return;
    }

    this.isClaimSubmitting = true;

    CancellationWorkflowRepository.claimRetentionOffer(claimRequest)
      .then((response) => {
        const result = response.data.result as ClaimCompletionResult;

        if (!result?.claimed) {
          this.showErrorNotification(result?.message || "We couldn’t apply the retention offer right now.");
          return;
        }

        this.claimedRetentionResult = result;
        this.hasClaimedRetentionOffer = true;
        this.closeClaimOfferModal();
        this.currentStep = "done";
        this.$emit("retention-selected", {
          exitSurvey: this.buildExitSurveyDto(true),
          claimRequest: null,
          claimed: true,
          result,
        });
      })
      .catch(() => {
        this.showErrorNotification("We couldn’t apply the retention offer right now.");
      })
      .finally(() => {
        this.isClaimSubmitting = false;
      });
  }

  skipOfferForNow() {
    this.currentStep = "reason";
  }

  async copyClaimCoupon() {
    if (!navigator?.clipboard?.writeText) {
      return;
    }

    await navigator.clipboard.writeText(this.claimOfferDetails.couponCode);
    this.couponCopied = true;
  }

  submitFeatureRequest() {
    if (!this.isFeatureRequestValid || this.isFeatureRequestSubmitting) {
      return;
    }

    this.submitFollowUpRequest({
      setSubmitting: (value) => {
        this.isFeatureRequestSubmitting = value;
      },
      submit: () => CancellationWorkflowRepository.submitFeatureRequest(this.buildFeatureRequestPayload()),
      onSuccess: (result) => {
        this.featureRequestSubmissionResult = result;
        this.featureRequestSubmitted = true;
      },
      successLogLabel: "Feature request accepted",
      failureMessage: "We couldn't send your feature request right now.",
    });
  }

  finishFeatureRequest() {
    this.acceptRetentionOffer();
    this.closeFeatureRequestModal();
  }

  closeFeatureRequestModal() {
    this.showFeatureRequestModal = false;
    this.featureRequestSubmitted = false;
    this.isFeatureRequestSubmitting = false;
    this.featureRequestSubmissionResult = null;
  }

  submitBookCall() {
    if (!this.isBookCallValid || this.isBookCallSubmitting) {
      return;
    }

    this.submitFollowUpRequest({
      setSubmitting: (value) => {
        this.isBookCallSubmitting = value;
      },
      submit: () => CancellationWorkflowRepository.submitSupportCallRequest(this.buildSupportCallRequestPayload()),
      onSuccess: (result) => {
        this.bookCallSubmissionResult = result;
        this.bookCallSubmitted = true;
      },
      successLogLabel: "Support call request accepted",
      failureMessage: "We couldn't send your call request right now.",
    });
  }

  finishBookCall() {
    this.acceptRetentionOffer();
    this.closeBookCallModal();
  }

  closeBookCallModal() {
    this.showBookCallModal = false;
    this.bookCallSubmitted = false;
    this.isBookCallSubmitting = false;
    this.bookCallSubmissionResult = null;
  }

  formatBookedCallDate(value: string) {
    if (!value) {
      return "your selected date";
    }

    return this.$moment(value).format("MMMM DD, YYYY");
  }

  formatRecipientList(recipients: string[]) {
    return recipients.join(", ");
  }

  openAddonChoiceModal(index: number) {
    if (
      this.showAddonChoiceModal
      && this.addonChoiceIndex === index
    ) {
      this.closeAddonChoiceModal();
      return;
    }

    this.addonChoiceIndex = index;
    this.showAddonChoiceModal = true;
  }

  buildDefaultWorkflowConfiguration() {
    return resolveWorkflowConfiguration(
      null,
      this.subscriptionPackageId,
      this.fallbackPackageName
    );
  }

  getNextStepAfterReasonSelection() {
    return this.hasConfiguredRetentionOffer ? "offer" : "confirm";
  }

  prefillContactDetails(target: { name: string; email: string }) {
    if (!target.name) {
      target.name = this.currentUserName;
    }

    if (!target.email) {
      target.email = this.currentUserEmail;
    }
  }

  showErrorNotification(message: string) {
    if (this.$snotify?.error) {
      this.$snotify.error(message);
    }
  }

  submitFollowUpRequest({
    setSubmitting,
    submit,
    onSuccess,
    successLogLabel,
    failureMessage,
  }: {
    setSubmitting: (value: boolean) => void;
    submit: () => Promise<any>;
    onSuccess: (result: any) => void;
    successLogLabel: string;
    failureMessage: string;
  }) {
    setSubmitting(true);

    submit()
      .then((response) => {
        const result = response.data.result;
        if (result?.accepted === false) {
          this.showErrorNotification(result.failureMessage || failureMessage);
          return;
        }

        onSuccess(result);
        if (process.env.NODE_ENV !== "production") {
          console.info(successLogLabel, result);
        }
      })
      .catch(() => {
        this.showErrorNotification(failureMessage);
      })
      .finally(() => {
        setSubmitting(false);
      });
  }

  trackRetentionAttempt(status: "viewed" | "cancelled" | "follow-up-pending" = "viewed") {
    if (!this.selectedReason || !this.resolvedWorkflowCard) {
      return;
    }

    CancellationWorkflowRepository.trackRetentionAttempt({
      restaurantId: this.effectiveRestaurantId,
      userId: this.effectiveUserId,
      subscriptionPackageId: this.effectiveSubscriptionPackageId,
      cancellationReason: this.selectedReason,
      workflowCardId: this.resolvedWorkflowCard.id,
      status,
      metadataJson: JSON.stringify({
        planName: this.planName,
        currentStep: this.currentStep,
      }),
    }).then((response) => {
    }).catch(() => {});
  }

  buildClaimRequest(): ClaimRetentionOfferDto | null {
    if (!this.selectedReason || !this.resolvedWorkflowCard) {
      return null;
    }

    return {
      restaurantId: this.effectiveRestaurantId,
      userId: this.effectiveUserId,
      subscriptionPackageId: this.effectiveSubscriptionPackageId,
      cancellationReason: this.selectedReason,
      selectedAddonJson: JSON.stringify(this.getSelectedAddonCodes()),
    };
  }

  buildOfferDetails(
    selectedOffer: any,
    fallbackTitle: string,
    packageName: string,
    fallbackContent: string,
    originalPrice: number | null,
    isCurrentPackage: boolean,
    hasConfiguredOriginalPrice: boolean = false
  ): RetentionOfferDetails {
    const couponCode = selectedOffer?.couponCode || "";
    const configuredDiscountedPrice = typeof selectedOffer?.discountedPrice === "number"
      ? selectedOffer.discountedPrice
      : null;
    const currentPayingAmount = typeof selectedOffer?.currentPayingAmount === "number"
      ? selectedOffer.currentPayingAmount
      : null;
    const canClaim = typeof selectedOffer?.canClaim === "boolean"
      ? selectedOffer.canClaim
      : true;
    const configuredDiscountCopy = selectedOffer?.couponDescription
      || this.getCouponDescription(couponCode)
      || "";
    const pricingReferencePrice = currentPayingAmount ?? originalPrice;
    const configuredDiscountPercent = typeof selectedOffer?.discountPercent === "number"
      ? selectedOffer.discountPercent
      : null;
    const historicalClaimedPrices = Array.isArray(selectedOffer?.historicalClaimedPrices)
      ? selectedOffer.historicalClaimedPrices.filter((price: unknown): price is number => typeof price === "number" && price > 0)
      : [];
    const inferredDiscountPercent = this.getOfferDiscountPercent(
      configuredDiscountCopy,
      couponCode,
      pricingReferencePrice,
      configuredDiscountedPrice,
      configuredDiscountPercent,
      historicalClaimedPrices
    );
    const calculatedDiscountedPrice = configuredDiscountedPrice !== null
      ? configuredDiscountedPrice
      : this.calculateDiscountedPrice(originalPrice, inferredDiscountPercent);
    const hasMeaningfulComparison = originalPrice !== null
      && calculatedDiscountedPrice !== null
      && calculatedDiscountedPrice < originalPrice;
    const discountedPrice = canClaim
      ? (calculatedDiscountedPrice ?? originalPrice)
      : (currentPayingAmount ?? originalPrice);
    const fallbackBullets = [
      "Keep your menus, labels, and data exactly as they are",
      "Your team stays up and running without missing a beat",
      "Claim bonus perks before your cancellation goes through",
    ];
    const discountContextLabel = hasMeaningfulComparison
      ? (hasConfiguredOriginalPrice ? "ON WHAT YOU PAY NOW" : "FROM STANDARD PACKAGE PRICE")
      : "PERSONALIZED NEXT RATE";

    return {
      title: fallbackTitle,
      packageName,
      content: isCurrentPackage ? fallbackContent : (selectedOffer?.content || fallbackContent),
      originalPrice: hasMeaningfulComparison ? originalPrice : null,
      discountedPrice,
      billingPeriod: selectedOffer?.billingPeriod || this.planBillingPeriodLabel,
      couponDescription: configuredDiscountCopy,
      couponCode,
      bullets: !isCurrentPackage && selectedOffer?.bullets?.length
        ? selectedOffer.bullets
        : fallbackBullets,
      discountPercentLabel: inferredDiscountPercent ? `${inferredDiscountPercent}% off` : configuredDiscountCopy,
      discountPercent: inferredDiscountPercent || null,
      discountContextLabel,
      currentPackageTag: isCurrentPackage ? "Current package" : "",
      isCurrentPackage,
      currentPayingAmount,
      canClaim,
      claimStatus: selectedOffer?.claimStatus || "",
      claimMessage: selectedOffer?.claimMessage || "",
      hasActiveRetentionOffer: !!selectedOffer?.hasActiveRetentionOffer,
      historicalClaimedPrices,
    };
  }

  getOfferComparisonPrices(offerDetails: OfferComparisonSource) {
    const comparisonPrices: number[] = [];
    const pushIfValid = (price: number | null | undefined) => {
      if (typeof price !== "number" || price <= 0 || comparisonPrices.includes(price) || price === offerDetails.discountedPrice) {
        return;
      }

      comparisonPrices.push(price);
    };

    pushIfValid(offerDetails.originalPrice);
    pushIfValid(offerDetails.currentPayingAmount);
    offerDetails.historicalClaimedPrices.forEach((price) => pushIfValid(price));

    return comparisonPrices.sort((left, right) => right - left);
  }

  getRetentionSupportingLine(selectedOffer: any) {
    const configuredDiscountCopy = selectedOffer?.couponDescription
      || this.getCouponDescription(selectedOffer?.couponCode || "")
      || "";
    const pricingReferencePrice = typeof selectedOffer?.currentPayingAmount === "number"
      ? selectedOffer.currentPayingAmount
      : (selectedOffer?.originalPrice ?? this.currentOfferBasePrice);
    const discountPercent = this.getOfferDiscountPercent(
      configuredDiscountCopy,
      selectedOffer?.couponCode || "",
      pricingReferencePrice,
      typeof selectedOffer?.discountedPrice === "number" ? selectedOffer.discountedPrice : null
    );
    const durationCopy = this.getOfferValidityLabel(
      selectedOffer?.validityPeriodCount,
      selectedOffer?.validityPeriodUnit
    );

    if (discountPercent && durationCopy) {
      return `Lock in ${discountPercent}% off your current renewal price and keep everything you rely on ${durationCopy}, no disruption, no downgrade.`;
    }

    if (discountPercent) {
      return `Lock in ${discountPercent}% off your current renewal price and keep everything you rely on, no disruption, no downgrade.`;
    }

    return "Lock in this offer and keep everything you rely on, no disruption, no downgrade.";
  }

  getOfferValidityLabel(validityPeriodCount?: number, validityPeriodUnit?: string) {
    const count = typeof validityPeriodCount === "number" && validityPeriodCount > 0
      ? validityPeriodCount
      : 0;

    if (!count) {
      return "";
    }

    const unit = validityPeriodUnit === "year" ? "year" : "month";
    return `for the next ${count} ${unit}${count === 1 ? "" : "s"}`;
  }

  getOfferDiscountPercent(
    discountCopy: string,
    couponCode: string,
    originalPrice: number | null,
    discountedPrice: number | null,
    configuredDiscountPercent: number | null = null,
    historicalClaimedPrices: number[] = []
  ) {
    if (configuredDiscountPercent && configuredDiscountPercent > 0) {
      return configuredDiscountPercent;
    }

    const percentFromCopy = this.extractPercent(discountCopy);
    if (percentFromCopy) {
      return percentFromCopy;
    }

    const percentFromCode = this.extractPercent(couponCode) || this.extractPercentFromCode(couponCode);
    if (percentFromCode) {
      return percentFromCode;
    }

    const lowestHistoricalPrice = historicalClaimedPrices.length
      ? Math.min(...historicalClaimedPrices)
      : null;
    const effectiveDiscountedPrice = discountedPrice ?? lowestHistoricalPrice;

    if (originalPrice && effectiveDiscountedPrice !== null && effectiveDiscountedPrice < originalPrice) {
      return Math.round(((originalPrice - effectiveDiscountedPrice) / originalPrice) * 100);
    }

    return 0;
  }

  extractPercent(value: string) {
    const matchedPercent = (value || "").match(/(\d+)\s*%/i);
    return matchedPercent ? Number(matchedPercent[1]) : 0;
  }

  extractPercentFromCode(couponCode: string) {
    const matchedCode = (couponCode || "").match(/SAVE(\d+)/i);
    return matchedCode && matchedCode[1] ? Number(matchedCode[1]) : 0;
  }

  calculateDiscountedPrice(originalPrice: number | null, discountPercent: number) {
    if (originalPrice === null || originalPrice === undefined || originalPrice <= 0) {
      return null;
    }

    if (!discountPercent) {
      return originalPrice;
    }

    return Math.max(0, Math.round((originalPrice * (100 - discountPercent))) / 100);
  }

  formatCurrency(amount: number | null | undefined) {
    if (amount === null || amount === undefined || Number.isNaN(amount)) {
      return "";
    }

    return `$${amount.toFixed(2)}`;
  }

  formatDiscountBadgeMain(value: string) {
    const safeValue = (value || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return safeValue.replace(/\s+off\b/i, '<span class="offer-discount-off"> OFF</span>');
  }

  getSelectedGiftValues() {
    if (!this.resolvedWorkflowCard) {
      return [];
    }

    return this.resolvedWorkflowCard.discountOffer.selectedGiftSlots || [];
  }

  getSelectedAddonCodes() {
    return Object.values(this.selectedDiscountedAddons).map((addon) => addon.value);
  }

  normalizeAddonValue(addonValue: AddOnType | string | null | undefined) {
    if (addonValue === null || addonValue === undefined) {
      return null;
    }

    if (typeof addonValue === "number") {
      return addonValue;
    }

    const normalizedAddonValue = addonValue.trim();

    if (normalizedAddonValue in AddOnType) {
      const enumValue = AddOnType[normalizedAddonValue as keyof typeof AddOnType];
      if (typeof enumValue === "number") {
        return enumValue;
      }
    }

    switch (normalizedAddonValue.toLowerCase()) {
    case "allergen-ai":
      return AddOnType.AllergenAI;
    case "recipe-cost":
      return AddOnType.RecipeCost;
    case "sugar-alcohols":
      return AddOnType.SugarAlcohols;
    case "wellness-analyzer":
      return AddOnType.WellnessModule;
    default:
      return null;
    }
  }

  getConfiguredGiftRewards() {
    const selectedOffer = this.resolvedWorkflowCard?.discountOffer;

    if (!selectedOffer || !selectedOffer.giftCount) {
      return [];
    }

    return Array.from({ length: selectedOffer.giftCount }, (_, index) => {
      const giftValue = selectedOffer.selectedGiftSlots[index];

      if (!giftValue) {
        return null;
      }

      return this.mapGiftValueToReward(
        giftValue,
        selectedOffer.selectedGiftDurations?.[index] || ""
      );
    }).filter((gift): gift is ReturnType<typeof this.mapGiftValueToReward> => Boolean(gift));
  }

  formatGiftDuration(duration: string) {
    if (!duration || !duration.trim()) {
      return "for the next 6 months";
    }

    if (duration.trim().toLowerCase() === "infinite") {
      return "with no end date";
    }

    return `for ${duration.trim()}`;
  }

  getGiftSlotDurationCopy(index: number) {
    const duration = this.resolvedWorkflowCard?.discountOffer?.selectedGiftDurations?.[index] || "";
    return this.formatGiftDuration(duration);
  }

  getAddonChoiceDescription(addonValue: AddOnType, index: number) {
    const durationCopy = this.getGiftSlotDurationCopy(index);

    switch (addonValue) {
    case AddOnType.AllergenAI:
      return `Add Allergen AI at no charge ${durationCopy}.`;
    case AddOnType.RecipeCost:
      return `Add Recipe Cost at no charge ${durationCopy}.`;
    case AddOnType.SugarAlcohols:
      return `Add Sugar Alcohol tools at no charge ${durationCopy}.`;
    case AddOnType.WellnessModule:
      return `Add Wellness Analyzer at no charge ${durationCopy}.`;
    default:
      return `Add this perk at no charge ${durationCopy}.`;
    }
  }

  mapGiftValueToReward(giftValue: string, duration: string) {
    const durationCopy = this.formatGiftDuration(duration);

    if (giftValue === "free-addon") {
      return {
        kind: "addon",
        title: "1 FREE ADDON",
        description: `Choose one add-on to include ${durationCopy}.`,
        icon: "fa fa-gift",
      };
    }

    if (giftValue === "priority-support") {
      return {
        kind: "perk",
        title: "Priority support",
        description: `Move the customer to a faster support lane ${durationCopy}.`,
        icon: "fa fa-life-ring",
      };
    }

    if (giftValue === "additional-15-discount") {
      return {
        kind: "perk",
        title: "Additional 15% discount",
        description: `Unlock an extra 15% discount on top of this offer ${durationCopy}.`,
        icon: "fa fa-percent",
      };
    }

    return {
      kind: "perk",
      title: "Free month extension",
      description: `Extend the subscription at no charge ${durationCopy}.`,
      icon: "fa fa-calendar-plus-o",
    };
  }

  closeAddonChoiceModal() {
    this.showAddonChoiceModal = false;
    this.addonChoiceIndex = null;
  }

  isAddonChoiceOpen(index: number) {
    return this.showAddonChoiceModal
      && this.addonChoiceIndex === index;
  }

  getSelectedAddonValues(index: number) {
    return Object.entries(this.selectedDiscountedAddons)
      .filter(([selectionIndex]) => Number(selectionIndex) !== index)
      .map(([, addon]) => addon.value);
  }

  isAddonChoiceDisabled(index: number, addonValue: AddOnType) {
    const currentSelection = this.selectedDiscountedAddons[index];

    if (currentSelection?.value === addonValue) {
      return false;
    }

    return this.getSelectedAddonValues(index).includes(addonValue);
  }

  getGiftAddonChoiceOptions(index: number) {
    return this.addonOptions.map((addon) => ({
      ...addon,
      disabled: this.isAddonChoiceDisabled(index, addon.value),
    }));
  }

  applyAddonChoice(index: number, addonValue: AddOnType) {
    if (this.isAddonChoiceDisabled(index, addonValue)) {
      return;
    }

    this.addonChoiceIndex = index;
    this.confirmAddonChoice(addonValue);
  }

  confirmAddonChoice(addonValue: AddOnType) {
    if (addonValue == null || this.addonChoiceIndex == null) {
      return;
    }

    const chosenAddon = this.addonOptions.find((addon) => addon.value === addonValue);
    if (!chosenAddon) {
      return;
    }

    const resolvedAddonChoice = {
      ...chosenAddon,
      description: this.getAddonChoiceDescription(addonValue, this.addonChoiceIndex),
    };

    this.selectedDiscountedAddons = {
      ...this.selectedDiscountedAddons,
      [this.addonChoiceIndex]: resolvedAddonChoice,
    };
    if (!this.isGiftRevealed(this.addonChoiceIndex)) {
      this.revealedGiftIndexes = [...this.revealedGiftIndexes, this.addonChoiceIndex];
    }
    if (!this.unboxedGiftIndexes.includes(this.addonChoiceIndex)) {
      this.unboxedGiftIndexes = [...this.unboxedGiftIndexes, this.addonChoiceIndex];
    }

    this.closeAddonChoiceModal();
  }

  openSupportChat() {
    this.trackRetentionAttempt("follow-up-pending");

    const supportWindow = window.open(
      "https://menusano.zendesk.com/hc/en-us/requests/new",
      "_blank",
      "noopener,noreferrer"
    );

    if (supportWindow) {
      supportWindow.opener = null;
    }
  }

  isGiftRevealed(index: number) {
    return this.revealedGiftIndexes.includes(index);
  }

  isGiftUnboxed(index: number) {
    return this.unboxedGiftIndexes.includes(index) || this.isGiftRevealed(index);
  }

  getGiftReveal(index: number) {
    return this.selectedDiscountedAddons[index] || this.giftRewards[index];
  }

  handleGiftClick(gift: { kind: string }, index: number) {
    if (gift.kind === "addon") {
      if (this.isGiftRevealed(index) || this.unboxedGiftIndexes.includes(index)) {
        return;
      }

      this.unboxedGiftIndexes = [...this.unboxedGiftIndexes, index];
      return;
    }

    if (this.isGiftRevealed(index)) {
      return;
    }

    this.revealedGiftIndexes = [...this.revealedGiftIndexes, index];
  }

  buildExitSurveyDto(wouldAcceptSpecialOffer: boolean): ExitSurveyDto {
    return {
      userId: this.effectiveUserId,
      subscriptionPackageId: this.effectiveSubscriptionPackageId,
      restaurantId: this.effectiveRestaurantId,
      cancellationReason: this.getCancellationReason(),
      lackingFeatureDescription: this.selectedReason === "missing-features" ? this.feedback : "",
      lackingCustomerSupportDescription: this.selectedReason === "poor-support-experience" ? this.feedback : "",
      otherReasonsDescription: this.selectedReason === "other" ? this.feedback : "",
      wouldAcceptSpecialOffer,
    };
  }

  buildFeatureRequestPayload(): SubmitFeatureRequestDto {
    return {
      restaurantId: this.effectiveRestaurantId,
      userId: this.effectiveUserId,
      subscriptionPackageId: this.effectiveSubscriptionPackageId,
      cancellationReason: this.selectedReason || "other",
      name: this.featureRequest.name.trim(),
      email: this.featureRequest.email.trim(),
      details: this.featureRequest.details.trim(),
    };
  }

  buildSupportCallRequestPayload(): SubmitSupportCallRequestDto {
    return {
      restaurantId: this.effectiveRestaurantId,
      userId: this.effectiveUserId,
      subscriptionPackageId: this.effectiveSubscriptionPackageId,
      cancellationReason: this.selectedReason || "other",
      name: this.supportBooking.name.trim(),
      email: this.supportBooking.email.trim(),
      phone: this.supportBooking.phone.trim(),
      availableDate: this.supportBooking.availableDate,
      details: this.supportBooking.details.trim(),
    };
  }

  buildCancellationFeedbackRequest(feedback: string): SubmitCancellationFeedbackDto {
    return {
      restaurantId: this.effectiveRestaurantId,
      userId: this.effectiveUserId,
      subscriptionPackageId: this.effectiveSubscriptionPackageId,
      cancellationReason: this.selectedReason || "other",
      name: this.currentUserName,
      email: this.currentUserEmail,
      feedback,
    };
  }

  getCancellationReason(): CancellationReason {
    switch (this.selectedReason) {
    case "too-expensive":
      return CancellationReason.TooExpensive;
    case "switching-solution":
      return CancellationReason.SwitchingPlatforms;
    case "missing-features":
      return CancellationReason.LackingFeatures;
    case "poor-support-experience":
      return CancellationReason.CustomerSupportIssues;
    case "not-using-enough":
    case "temporary-closure":
      return CancellationReason.NoLongerNeeded;
    default:
      return CancellationReason.Other;
    }
  }
}
